import React, { useState } from 'react';
import { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DashboardView from '../../../../../../theme/ui/Dashboard/DashboardView/DashboardView';
import DashboardHeader from '../../../../../../theme/ui/Dashboard/DashboardView/DashboardHeader';
import useTypeEntity from '../useTypeEntity';
import { useUserSettingState } from '../../../../../../redux/user/UserSettingsAPI/hook/useUserSettingState';
import { useLocation } from 'react-router-dom';
import WorkspaceAPI from '../../../../../../redux/workspace/WorkspaceAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import useListPanels from './useListPanels';
import Button from '../../../../../../theme/ui/Atoms/Button';
import DashboardAddPanelModal from '../../../../../../theme/ui/Dashboard/DashboardView/DashboardAddPanelModal';

interface EntityDashboardProps {
  workspaceId?: string;
  groupId?: string;
}

function EntityDashboard({ workspaceId, groupId }: EntityDashboardProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [rowHeight] = useState(90);
  const [xsColumns] = useState(6);
  const { type } = useTypeEntity();
  WorkspaceAPI.useGetWorkspaceByIdQuery(workspaceId ?? skipToken);
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/markets');
  const typeRequest = groupId
    ? 'group'
    : workspaceId
    ? 'workspace'
    : isAdmin
    ? 'workspaces'
    : 'user';

  const panels = useListPanels(typeRequest, workspaceId, groupId);
  const defaultLayout: Layout = [
    { i: '1', x: 0, y: 0, w: 3, h: 2, moved: false, static: true },
    { i: '2', x: 3, y: 0, w: 3, h: 2, moved: false, static: true },
    // {
    //   i: 'panel-divider-1',
    //   x: 0,
    //   y: 2,
    //   w: 6,
    //   h: 1,
    //   moved: false,
    //   static: true,
    // },
    // { i: '3', x: 0, y: 3, w: 3, h: 2, moved: false, static: true },
    // { i: '4', x: 3, y: 3, w: 3, h: 2, moved: false, static: true },
    // { i: '5', x: 0, y: 5, w: 3, h: 2, moved: false, static: true },
    // { i: '6', x: 3, y: 5, w: 3, h: 2, moved: false, static: true },
    // { i: '7', x: 0, y: 7, w: 6, h: 2, moved: false, static: true },
    // { i: '101', x: 0, y: 9, w: 6, h: 1, moved: false, static: true },
    // { i: '102', x: 3, y: 13, w: 3, h: 2, moved: false, static: true },
    // { i: '103', x: 2, y: 12, w: 1, h: 1, moved: false, static: true },
    // { i: '104', x: 3, y: 12, w: 2, h: 1, moved: false, static: true },
    // { i: '105', x: 0, y: 22, w: 6, h: 3, moved: false, static: true },
    // { i: '106', x: 0, y: 12, w: 2, h: 1, moved: false, static: true },
    // { i: '107', x: 0, y: 10, w: 6, h: 2, moved: false, static: true },
    // { i: '108', x: 0, y: 15, w: 3, h: 2, moved: false, static: true },
    // { i: '109', x: 0, y: 13, w: 2, h: 2, moved: false, static: true },
    // { i: '110', x: 3, y: 15, w: 3, h: 2, moved: false, static: true },
    // { i: '111', x: 0, y: 17, w: 6, h: 5, moved: false, static: true },
  ];
  const scope = `${type}:${groupId ?? workspaceId ?? '*'}`;
  const {
    data: layout,
    setData: setLayout,
    isLoading: isLoadingLayout,
  } = useUserSettingState(`workspaceDashboardLayout:${scope}`, defaultLayout);
  // availablePanels must be defined after both panels and layout
  const availableToAddPanels = React.useMemo(
    () => panels.filter((p) => !layout?.some((l) => l.i === p.id)),
    [layout, panels]
  );

  const visiblePanels = React.useMemo(
    () => panels.filter((p) => layout?.some((l) => l.i === p.id)),
    [layout, panels]
  );

  const handleLayoutChange = (newLayout: Layout) => {
    if (isEditMode) {
      setLayout(newLayout.map((item) => ({ ...item, static: !isEditMode })));
    }
  };

  const onToggleEditMode = () => {
    if (layout)
      setLayout(
        layout.map((item) => ({ ...item, static: isEditMode })),
        isEditMode
      );
    setIsEditMode(!isEditMode);
  };

  // reset edit mode
  React.useEffect(() => {
    setIsEditMode(false);
  }, [groupId, workspaceId]);

  const handleRemovePanel = (id: string) => {
    layout && setLayout(layout.filter((l) => l.i !== id));
  };

  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const handleAddPanelClick = () => {
    setIsAddPanelOpen(true);
  };
  const handleCloseAddPanel = () => {
    setIsAddPanelOpen(false);
  };
  const handleSavePanel = (panelId: string) => {
    const y = layout ? Math.max(...layout.map((l) => l.y + l.h)) : 0;
    setLayout([
      ...(layout || []),
      { i: panelId, x: 0, y, w: 3, h: 2, static: !isEditMode },
    ]);
    setIsAddPanelOpen(false);
  };
  // const title =
  //   entity?.name || workspace?.name
  //     ? `${entity?.name || workspace?.name} ${t('dashboard.title')}`
  //     : t('dashboard.title');
  return (
    <DashboardView
      panels={visiblePanels}
      layout={layout}
      isEditMode={isEditMode}
      rowHeight={rowHeight}
      xsColumns={xsColumns}
      theme={theme}
      isLoadingLayout={isLoadingLayout}
      onRemovePanel={handleRemovePanel}
      onLayoutChange={handleLayoutChange}
      header={
        <>
          {/* <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h4">{title}</Typography>
          </Box> */}
          <DashboardHeader
            isEditMode={isEditMode}
            onToggleEditMode={onToggleEditMode}
            loading={isLoadingLayout}
          />

          {isEditMode && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddPanelClick}
              disabled={availableToAddPanels.length === 0}
            >
              {t('dashboard.add_panel', 'Add Panel')}
            </Button>
          )}
          <DashboardAddPanelModal
            open={isAddPanelOpen}
            onClose={handleCloseAddPanel}
            onSavePanel={handleSavePanel}
            availablePanels={availableToAddPanels}
          />
        </>
      }
    />
  );
}

export default EntityDashboard;
